/**
 * Rig board rules (ENG-946) — the pure logic behind the raw MetaHuman control
 * board, ported from the TexAvatars research viewer (sarper/TexAvatars,
 * web/src/{ui/SliderPanel,main}.ts) so our board behaves identically.
 *
 * Everything here is a pure function of the BUNDLE's own data — control names,
 * the trained pose table (`rig_presets.npy`), and the `rig_range` / `macros` /
 * `coupling` fields of `scene.json`. Nothing is hardcoded per character: a
 * bundle that declares different controls, ranges or macros gets a different
 * board, which is the point.
 *
 * Why the non-obvious parts exist (all measured on the eyeline checkpoints, per
 * the research viewer's comments):
 *
 *  - TRAINED-DOMAIN LIMITS. A slider spanning the full rig_range drives most
 *    controls out of distribution, where the decoders extrapolate into
 *    artifacts. The limits are the per-control min/max actually seen in
 *    training, padded slightly.
 *  - COUPLING. The rig encoder attributes face state to control
 *    CONFIGURATIONS, so dragging one control alone barely moves the latent —
 *    the mesh closes but the decoded appearance stays put. Co-moving the
 *    controls that accompany a driver in the training data keeps a
 *    single-slider edit on-manifold.
 *  - MACROS. "Close the eyes" / "open the mouth" are not single controls but
 *    configurations (blink + lidPress + eyelidU + gaze …). A macro lerps that
 *    SUBSET from wherever the pose currently is toward the trained reference
 *    configuration, so it closes correctly from any base pose.
 */

/** 'CTRL_L_mouth_cornerPull.translateY' → 'L mouth cornerPull Y' */
export function prettyRigLabel(raw) {
  if (typeof raw !== 'string') return ''
  return raw
    .replace(/^CTRL_/, '')
    .replace(/\.(translate|rotate)([XYZ])$/, ' $2')
    .replace(/_/g, ' ')
    .trim()
}

/** One pose row out of the flat presets table. */
export function poseRow(presets, dim, index) {
  const rows = Math.floor(presets.length / dim)
  const i = Math.max(0, Math.min(rows - 1, index | 0))
  return presets.slice(i * dim, (i + 1) * dim)
}

export function poseCount(presets, dim) {
  if (!presets || !dim) return 0
  return Math.floor(presets.length / dim)
}

/**
 * The per-control box a UI should bound its sliders by, under the session's
 * `?rigclamp`.
 *
 * ONE resolver for both UIs. The Rig Preview board and the live ARKit path have
 * to agree about how far a control may be driven, or the same pose blows up or
 * does not depending only on which one produced it — the split ENG-946 closed.
 * `?rigclamp` therefore has to move both, and it did not: the board's sliders
 * kept their `max` at the trained box, so on a narrow capture a jaw slider
 * stopped at 0.269 with the flag saying nothing clamps. That reads as the flag
 * being broken.
 *
 * Null means "no per-control box" — the caller falls back to the declared
 * `rig_range`. That is the widest bound a SLIDER can have (a widget needs finite
 * ends), so `declared` and `none` look the same here while they differ on the
 * live path, where `none` really is unbounded.
 *
 * @param {{presets: Float32Array|null, dim: number, range: [number, number],
 *          clamp?: 'trained'|'declared'|'none', pad?: number|null}} args
 */
export function boardLimits({ presets, dim, range, clamp = 'trained', pad = null }) {
  if (!presets || clamp !== 'trained') return null
  return trainedLimits(presets, dim, range, pad ?? undefined)
}

/**
 * Per-control [min,max] actually covered by the trained poses, padded and
 * clamped to the bundle's rig_range. Returns null when there is no pose table
 * to derive from — the caller then falls back to the full range.
 */
export function trainedLimits(presets, dim, range, pad = 0.03) {
  const rows = poseCount(presets, dim)
  if (!rows) return null
  const [lo0, hi0] = range
  const out = []
  for (let i = 0; i < dim; i++) {
    let lo = Infinity, hi = -Infinity
    for (let r = 0; r < rows; r++) {
      const v = presets[r * dim + i]
      if (v < lo) lo = v
      if (v > hi) hi = v
    }
    out.push([Math.max(lo0, lo - pad), Math.min(hi0, hi + pad)])
  }
  return out
}

/**
 * Co-move a driver's companion controls. Mutates `rig` and reports whether it
 * did anything.
 *
 * Applied ONLY to a pure single-control change: if more than one control moved
 * this is a programmatic multi-control update (a macro, a pose recall) that is
 * already on-manifold and must be left alone.
 */
export function applyCoupling(rig, prev, specs, limits, range) {
  if (!specs || !specs.length) return false
  let changed = -1
  for (let i = 0; i < rig.length; i++) {
    if (rig[i] !== prev[i]) {
      if (changed >= 0) return false
      changed = i
    }
  }
  if (changed < 0) return false
  const spec = specs.find(s => s.driver === changed)
  if (!spec) return false

  const delta = rig[changed] - prev[changed]
  for (const key of Object.keys(spec.slopes)) {
    const i = Number(key)
    if (!Number.isInteger(i) || i < 0 || i >= rig.length) continue
    const lim = limits?.[i]
    const lo = lim ? lim[0] : range[0]
    const hi = lim ? lim[1] : range[1]
    rig[i] = Math.min(hi, Math.max(lo, rig[i] + spec.slopes[key] * delta))
  }
  return true
}

// Control-name fragments that make up each macro's configuration. Straight from
// the research viewer — these are properties of the MetaHuman control naming,
// not of any one character.
const MOUTH_FRAGS = ['jaw', 'mouth']
const EYE_FRAGS = [
  'CTRL_C_eye.', 'parallelLook', '_eye.translate', '_eyeAim.',
  '_eye_blink', '_eye_cheekRaise', '_eye_eyelidD', '_eye_eyelidU',
  '_eye_faceScrunch', '_eye_lidPress', '_eye_pupil', '_eye_squintInner',
]

/**
 * Controls a macro drives, optionally restricted to one SIDE.
 *
 * `side` is 'L' or 'R'; anything else takes both. The eyes need it: one slider
 * for both eyes cannot express a wink, and closing the left eye dragging the
 * right shut with it is simply wrong. Controls with no side in their name
 * (`CTRL_C_eye.`, `parallelLook` — the shared gaze) belong to NEITHER side and
 * are dropped from a sided subset rather than duplicated into both, or the two
 * sliders would fight over them.
 */
export function macroSubset(names, kind, side = null) {
  const frags = kind === 'eyes' ? EYE_FRAGS : MOUTH_FRAGS
  const want = side === 'L' || side === 'R' ? `_${side}_` : null
  const out = []
  names.forEach((n, i) => {
    if (!frags.some(f => n.includes(f))) return
    if (want && !n.includes(want)) return
    out.push(i)
  })
  return out
}

/**
 * Build a bidirectional macro over a control subset.
 *
 * `openRef` / `closedRef` are two trained pose rows. `valueOf(rig)` projects a
 * rig onto the closed→open axis, so the macro slider starts wherever the
 * current pose already sits; `apply(base, t, t0)` lerps the subset from the
 * base pose toward whichever reference the user is heading for.
 *
 * Returns null when the bundle can't support the macro (no subset, refs out of
 * range) — a missing macro is a missing slider, never a fake one.
 */
export function buildMacro({ subset, openRef, closedRef }) {
  if (!subset?.length || !openRef || !closedRef) return null
  let axisLen2 = 0
  for (const i of subset) axisLen2 += (openRef[i] - closedRef[i]) ** 2
  if (!(axisLen2 > 0)) return null

  const valueOf = (rig) => {
    let dot = 0
    for (const i of subset) dot += (rig[i] - closedRef[i]) * (openRef[i] - closedRef[i])
    return Math.max(0, Math.min(1, dot / axisLen2))
  }

  /** @returns {Float32Array} a NEW rig with the subset moved; other controls copied. */
  const apply = (base, t, t0) => {
    const out = Float32Array.from(base)
    const toward = t >= t0 ? openRef : closedRef
    const denom = t >= t0 ? Math.max(1 - t0, 1e-3) : Math.max(t0, 1e-3)
    const mix = Math.min(1, Math.abs(t - t0) / denom)
    for (const i of subset) out[i] = (1 - mix) * base[i] + mix * toward[i]
    return out
  }

  return { subset, valueOf, apply }
}

/**
 * Normalize scene.json's `mouth_controller` block (snake_case, as exported) into
 * the camelCase spec the controller uses. Returns null when the bundle can't
 * support it — a missing controller is a missing slider, never a fake one, and a
 * bundle without the block keeps the global mouth macro above.
 *
 * `quality` / `wQuality` are optional: bundles exported before donor scoring
 * carry neither, and default to 0, which reduces the cost function to pure
 * proximity (the original behaviour).
 *
 * `w_quality` is PER POOL — an object `{closed, open}` — because the two sides
 * measure different things (see pickMouthReference). A bare number applies to
 * both, which is how eyeline's bundles are written, and is normalized to the
 * object here so there is one shape downstream.
 */
export function parseMouthController(raw) {
  if (!raw || typeof raw !== 'object') return null
  const ints = (v) => (Array.isArray(v) ? v.map(Number).filter(Number.isInteger) : [])
  // Non-finite quality poisons the whole score (NaN < best is always false), so a
  // single bad entry would drop the controller entirely. Unscored rows are
  // documented as 0 — treat unparseable ones the same way.
  const nums = (v) => (Array.isArray(v) ? v.map((x) => (Number.isFinite(Number(x)) ? Number(x) : 0)) : [])
  // Same failure mode for the WEIGHTS, and it costs the whole controller rather
  // than one row: a non-finite weight makes every candidate score NaN, every
  // comparison false, and pickMouthReference returns -1 for BOTH pools — so
  // buildMouthController yields null and the slider silently never renders.
  const weight = (v, dflt) => (v === undefined || !Number.isFinite(Number(v)) ? dflt : Number(v))
  // Per-pool object, or a bare number meaning both. Reading the object through
  // Number() is what hid isaac-v6's controller: NaN, and the row disappeared.
  const poolWeights = (v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return { closed: weight(v.closed, 0), open: weight(v.open, 0) }
    }
    const both = weight(v, 0)
    return { closed: both, open: both }
  }
  const spec = {
    jaw: Number(raw.jaw),
    wEye: weight(raw.w_eye, 0.5),
    wQuality: poolWeights(raw.w_quality),
    quality: nums(raw.quality),
    mouthIdx: ints(raw.mouth_idx),
    eyesIdx: ints(raw.eyes_idx),
    restIdx: ints(raw.rest_idx),
    closedPool: ints(raw.closed_pool),
    openPool: ints(raw.open_pool),
  }
  const usable = Number.isInteger(spec.jaw) && spec.jaw >= 0
    && spec.mouthIdx.length > 0
    && spec.closedPool.length > 0
    && spec.openPool.length > 0
  return usable ? spec : null
}

/**
 * Pick the trained reference row in `pool` nearest to `pose`.
 *
 * Cost = √dRest + wEye·√dEye − wQuality[poolKind]·quality[row].
 *
 * The rest term is the base state the mouth will be composed onto. The EYE term
 * is down-weighted rather than dropped because open-mouth × closed-eye is the
 * sparsest cell in this data, so scoring eyes at full weight drags every open
 * reference toward a handful of rows.
 *
 * The QUALITY term is not a tie-break: proximity alone picks badly. Measured
 * (texavatars tools/v10/score_donors.py) the nearest open reference scored
 * 2nd-worst of 17 donors on the worst browser case, and switching to the best
 * moved rendered tooth signal from 56% to 82% of the trained curve, while the
 * interpolation coordinate was worth ~1.5 points. Rows never scored carry 0, so
 * an unscored bundle degrades to pure proximity rather than to noise.
 *
 * Its WEIGHT is per pool, because the two sides measure different things. A
 * closed donor is scored on tooth LEAK, which says nothing about whether the
 * grafted mouth suits the base's expression — so proximity keeps that side and
 * quality is a tie-break (isaac measured 0.05). An open donor has one globally
 * best answer, so the weight is decisive there (3.0; below ~2.73 it doesn't
 * reach at all) and the open reference stops being base-conditioned. Rationale
 * and measurements: texavatars characters/isaac/docs/ISAAC_CHANGELOG.md §10.7.
 */
export function pickMouthReference(spec, pose, pool, presets, dim, poolKind = 'closed') {
  if (!pool?.length || !presets || !dim) return -1
  const rows = poseCount(presets, dim)
  const wQuality = spec.wQuality?.[poolKind] ?? 0
  let best = -1
  let bestScore = Infinity
  for (const r of pool) {
    if (!Number.isInteger(r) || r < 0 || r >= rows) continue
    const o = r * dim
    let dRest = 0
    for (const i of spec.restIdx) { const d = presets[o + i] - pose[i]; dRest += d * d }
    let dEye = 0
    for (const i of spec.eyesIdx) { const d = presets[o + i] - pose[i]; dEye += d * d }
    const q = spec.quality[r] ?? 0
    const score = Math.sqrt(dRest) + spec.wEye * Math.sqrt(dEye) - wQuality * q
    if (score < bestScore) { bestScore = score; best = r }
  }
  return best
}

/**
 * Base-conditioned mouth controller — the successor to the global mouth macro.
 *
 * The macro nudges companion controls along ONE global closed→open trajectory
 * and keeps the base pose's own mouth residuals, so an edit lands on control
 * combinations the model never saw. This instead picks the closed and open
 * TRAINED references nearest the current pose and REPLACES the whole mouth block
 * with an interpolation between them, which keeps every intermediate on the
 * training manifold. Measured: opening from a nearly-closed base goes from 54%
 * to 89% of trained rendered tooth visibility.
 *
 * References resolve from `pose` — the base the user started from — NOT from the
 * live rig, so repeated drags are idempotent rather than walking the references
 * along with their own output.
 *
 * Returns null when the bundle can't support it (no spec, no pose table, pools
 * that don't resolve), so the caller falls back to the macro.
 */
export function buildMouthController({ spec, pose, presets, dim }) {
  if (!spec || !pose || !presets || !dim) return null
  const closedRow = pickMouthReference(spec, pose, spec.closedPool, presets, dim, 'closed')
  const openRow = pickMouthReference(spec, pose, spec.openPool, presets, dim, 'open')
  if (closedRow < 0 || openRow < 0) return null

  const closedRef = poseRow(presets, dim, closedRow)
  const openRef = poseRow(presets, dim, openRow)
  const subset = spec.mouthIdx.filter(i => i >= 0 && i < dim)
  if (!subset.length) return null

  // Aperture is read off the JAW control alone, not projected over the whole
  // block: the block is REPLACED wholesale, so the only coordinate that has to
  // round-trip is the one the user is dragging.

  const jawLo = closedRef[spec.jaw]
  const jawHi = openRef[spec.jaw]
  const span = jawHi - jawLo
  const valueOf = (rig) => (Math.abs(span) < 1e-6
    ? 0
    : Math.max(0, Math.min(1, (rig[spec.jaw] - jawLo) / span)))

  /** @returns {Float32Array} a NEW rig: mouth block interpolated, rest copied. */
  const apply = (base, t) => {
    const out = Float32Array.from(base)
    const mix = Math.max(0, Math.min(1, t))
    for (const i of subset) out[i] = (1 - mix) * closedRef[i] + mix * openRef[i]
    return out
  }

  return { subset, closedRow, openRow, valueOf, apply }
}

/**
 * The eyes macro's references are DERIVED, not declared: closed = the pose with
 * the most blink; open = the pose that minimises blink while keeping gaze near
 * the dataset mean (min-blink alone picks rows whose eyes are rolled up, which
 * reads as all sclera).
 */
export function deriveEyeRefs(names, presets, dim) {
  const rows = poseCount(presets, dim)
  if (!rows) return null
  const blinkL = names.findIndex(n => n.includes('CTRL_L_eye_blink'))
  const blinkR = names.findIndex(n => n.includes('CTRL_R_eye_blink'))
  if (blinkL < 0 || blinkR < 0) return null

  const gazeFrags = ['CTRL_C_eye.', 'parallelLook', '_eye.translate', '_eyeAim.']
  const gazeIdx = []
  names.forEach((n, i) => { if (gazeFrags.some(f => n.includes(f))) gazeIdx.push(i) })
  const gazeMean = gazeIdx.map(i => {
    let s = 0
    for (let r = 0; r < rows; r++) s += presets[r * dim + i]
    return s / rows
  })

  let closedRow = 0, bestBlink = -Infinity
  let openRow = 0, bestOpen = Infinity
  for (let r = 0; r < rows; r++) {
    const blink = (presets[r * dim + blinkL] + presets[r * dim + blinkR]) / 2
    if (blink > bestBlink) { bestBlink = blink; closedRow = r }
    let gazeDev = 0
    gazeIdx.forEach((i, k) => { gazeDev += (presets[r * dim + i] - gazeMean[k]) ** 2 })
    const score = blink + gazeDev
    if (score < bestOpen) { bestOpen = score; openRow = r }
  }
  return { openRow, closedRow }
}



/**
 * The ARKit BASE pose: `pose`'s own face, with its mouth block replaced by the
 * nearest closed TRAINED mouth.
 *
 * WHY THIS EXISTS. One vector was doing two unrelated jobs. The bundle's rest rig
 * is pose-table row 0 because that is the pose whose baked mesh IS
 * `neutral_vertices` — a GEOMETRY fact, which `calibrate()` must anchor at or the
 * whole splat field is misplaced. Its EXPRESSION is just whatever the actor's face
 * was doing on capture frame 0, and on eyeline-v10c that is a slightly open mouth
 * (jaw 0.173, against a closed pool running 0.003-0.063 — row 0 sits at the 100th
 * percentile of "closed").
 *
 * The ARKit path then composed onto that same vector, and `arkitToMh` emits no
 * NEGATIVE deltas for any mouth channel (only the eye-look, eye-wide and
 * jaw-forward/left channels go negative). So `rest + delta` could never close the
 * mouth past row 0's own aperture: the live face had a floor above every closed
 * configuration the character was captured in, and `MouthClose=1` moved the jaw by
 * exactly nothing.
 *
 * Grafting only the MOUTH BLOCK is what keeps that from costing anything else:
 * switching wholesale to the nearest closed row moves the non-mouth face by up to
 * 0.522 (different eyes, different brows), while this leaves every non-mouth
 * control exactly as row 0 had it. Choosing the donor with `pickMouthReference`
 * is the same base-conditioned pick the mouth controller uses — the research
 * viewer's design is precisely that a mouth block may be swapped onto a base whose
 * rest/eye configuration it matches.
 *
 * Returns null when the bundle can't support it; the caller then keeps `pose`.
 */
export function graftClosedMouth({ spec, pose, presets, dim }) {
  if (!spec || !pose || !presets || !dim) return null
  // DONOR RULE: among the closed pool, the mouth that differs LEAST from the base's
  // own. Not `pickMouthReference` — that scores rest/eye proximity plus measured
  // donor quality, which is right for choosing a mouth to OPEN into and wrong here.
  // Asked that way it picked row 201 on eyeline-v10c: closed, and a broad grin with
  // the teeth showing, 2.081 away from row 0's mouth. The pools are an aperture
  // percentile — "closed" means the lips are not parted, which a wide smile
  // satisfies — so the neutral expression has to be asked for separately. Ranking
  // by mouth proximity picks a mouth 0.683 away, at jaw 0.026 instead of 0.173.
  const rowsN = poseCount(presets, dim)
  let row = -1
  let best = Infinity
  for (const r of spec.closedPool) {
    if (!Number.isInteger(r) || r < 0 || r >= rowsN) continue
    const o = r * dim
    let d = 0
    for (const i of spec.mouthIdx) {
      if (i < 0 || i >= dim) continue
      const e = presets[o + i] - pose[i]
      d += e * e
    }
    if (d < best) { best = d; row = r }
  }
  if (row < 0) return null
  const donor = poseRow(presets, dim, row)
  const out = Float32Array.from(pose)
  let grafted = 0
  for (const i of spec.mouthIdx) {
    if (!Number.isInteger(i) || i < 0 || i >= dim) continue
    out[i] = donor[i]
    grafted++
  }
  return grafted ? { rig: out, row, grafted } : null
}


/**
 * Blink as a CONFIGURATION, not a control.
 *
 * ARKit's blink drives one control per eye. Every trained blink row moves a whole
 * group with it — lid press, upper/lower eyelid, cheek raise, face scrunch — so
 * "blink high, companions at rest" is a combination the capture never contained.
 * The trained-subspace projection then pulls it most of the way back: measured on
 * eyeline-v10c, a full ARKit blink reaches 0.710 after the clamp and lands at
 * 0.381 after the projection, which is an eye that never quite shuts. Driving the
 * group instead survives at 0.660.
 *
 * SCOPE IS THE LIDS, NOT THE EYES. The existing eyes macro subset is 27 controls
 * and includes gaze (`eyeAim`, `translateX`, `parallelLook`, `pupil`); replacing
 * all of it would snap the eyes' DIRECTION on every blink — the same mistake the
 * wholesale mouth controller made, which left 30 of ARKit's 52 channels inert.
 * This takes the 14 lid controls and leaves gaze entirely alone (verified: a gaze
 * offset survives a full blink unchanged).
 *
 * PER SIDE, so a wink is still a wink.
 *
 * The interpolation runs from the LIVE rig toward the closed reference, not from
 * the base: at t=0 it is exactly a no-op, so whatever squint or widen the ARKit
 * path put on those controls survives until a blink actually starts closing over
 * it — which is what a closing eye does anyway.
 *
 * @returns {null|{count:number, closedRow:number, applyInto:(rig, tL, tR) => void}}
 */
export function buildBlinkMacro({ names, presets, dim }) {
  if (!names?.length || !presets || !dim) return null
  const refs = deriveEyeRefs(names, presets, dim)
  if (!refs) return null
  const LID = /(blink|eyelid|lidPress|cheekRaise|faceScrunch|squint)/i
  const GAZE = /(translateX|eyeAim|parallelLook|pupil)/i
  const pick = (side) => names
    .map((n, i) => [n, i])
    .filter(([n]) => LID.test(n) && !GAZE.test(n) && n.includes(`_${side}_`))
    .map(([, i]) => i)
    .filter((i) => i < dim)
  const left = pick('L')
  const right = pick('R')
  if (!left.length && !right.length) return null
  const closed = poseRow(presets, dim, refs.closedRow)

  const applyInto = (rig, tL, tR) => {
    const a = tL > 0 ? (tL < 1 ? tL : 1) : 0
    const b = tR > 0 ? (tR < 1 ? tR : 1) : 0
    if (a > 0) for (const i of left) rig[i] += a * (closed[i] - rig[i])
    if (b > 0) for (const i of right) rig[i] += b * (closed[i] - rig[i])
  }
  return { count: left.length + right.length, closedRow: refs.closedRow, applyInto }
}
